import {
  AlertCircle,
  BarChart3,
  Brain,
  Calendar,
  Download,
  FolderOpen,
  Home,
  LogOut,
  Mail,
  Menu,
  Monitor,
  Settings,
  Users,
  X,
  ArrowLeft,
  Lock,
  Moon,
  Sun,
  MessageCircle,
  FileText,
  Zap,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Target,
  CheckSquare,
  Briefcase
} from 'lucide-react';
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo/Logo';
import { useApi } from '../../hooks/useApi';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { Skeleton, Modal } from 'antd';
import { AlertTriangle, Save } from 'lucide-react';
import FloatingChatButton from '../FloatingChatButton/FloatingChatButton';
import FloatingSuggestButton from '../FloatingSuggestButton/FloatingSuggestButton';
import { useGoalsSettings } from '../../contexts/GoalsSettingsContext';
import { isS5Organisation } from '../../utils/organization';
import { canUserAccessCrm } from '../../modules/crm/utils/orgGating';


const SIDEBAR_COLLAPSED_KEY = 'tg.sidebarCollapsed';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch (_) {}
      return next;
    });
  }, []);
  const [organization, setOrganization] = useState(null);
  const [orgLoaded, setOrgLoaded] = useState(false);
  const mainRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  // Scroll main content to top on route change (dashboard/app pages)
  useLayoutEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);
  const { mode, toggle } = useTheme();
  const isDark = mode === 'dark';
  
  // Get goals settings - context should be available as Layout is wrapped in GoalsSettingsProvider
  const goalsSettingsContext = useGoalsSettings();
  const goalsSettings = goalsSettingsContext?.settings || { myGoalsEnabled: false, myTasksEnabled: false };
  const goalsSettingsLoaded = goalsSettingsContext?.loaded ?? false;
  const hasUnsavedGoalsChanges = goalsSettingsContext?.hasUnsavedChanges ?? false;
  const resetGoalsToSaved = goalsSettingsContext?.resetToSaved;
  const saveGoalsSettings = goalsSettingsContext?.saveSettings;

  // Unsaved changes guard state (for navigating away from Goal Settings)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [savingOnLeave, setSavingOnLeave] = useState(false);
  const pendingNavHref = useRef(null);

  // Goals & Tasks section - default collapsed
  const isGoalsActive = location.pathname === '/dashboard/goals' || location.pathname === '/dashboard/tasks';
  const [isGoalsExpanded, setIsGoalsExpanded] = useState(false);

  // Fetch organization data to check plan
  const fetchOrganization = useCallback(async (forceRefresh = false) => {
    if (user && user.id) {
      try {
        // Clear cache if force refresh
        if (forceRefresh && typeof window !== 'undefined') {
          try {
            delete window.__orgProfile;
            delete window.__orgProfileFetchedAt;
          } catch (_) {}
        }
        
        // Always fetch fresh data (don't use cache) to ensure latest bypass status
        const response = await api.get('/api/v1/billing/profile');
        if (response.data.success) {
          const orgData = response.data.data;
          console.log('🔍 [LAYOUT] Organization data loaded:', {
            paymentBypassEnabled: orgData.paymentBypassEnabled,
            orgPlanSelection: orgData.orgPlanSelection,
            subscriptionStatus: orgData.subscriptionStatus,
            userIsActivated: orgData.userIsActivated,
            fullData: orgData
          });
          setOrganization(orgData);
          try {
            window.__orgProfile = orgData;
            window.__orgProfileFetchedAt = Date.now();
          } catch (_) {}
        }
        setOrgLoaded(true);
      } catch (error) {
        console.error('Error fetching organization data:', error);
        setOrgLoaded(true);
      }
    }
  }, [user?.id]); // Removed 'api' - it's stable via useMemo in useApi hook

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  // Listen for payment success to refresh organization data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const orgId = urlParams.get('orgId');

    if (payment === 'success' && orgId) {
      // Clear cache and sync subscription from Stripe
      if (typeof window !== 'undefined') {
        try {
          delete window.__orgProfile;
          delete window.__orgProfileFetchedAt;
        } catch (_) {}
      }
      
      // Try to manually sync subscription from Stripe
      const syncSubscription = async () => {
        try {
          await api.post('/api/v1/billing/sync-subscription');
          console.log('✅ [LAYOUT] Manual subscription sync completed');
        } catch (syncError) {
          console.warn('⚠️ [LAYOUT] Manual sync failed, will rely on webhook:', syncError);
        }
      };
      
      // Sync immediately
      syncSubscription();
      
      // Clear cache and refresh after webhook processes
      setTimeout(async () => {
        await syncSubscription();
        await fetchOrganization(true);
        // Refresh again to ensure data is synced
        setTimeout(async () => {
          await syncSubscription();
          await fetchOrganization(true);
        }, 2000);
      }, 1000);
    }
  }, [location.search, fetchOrganization, api]);

  // Redirect main admin users to main admin panel (unless they are in switch mode)
  useEffect(() => {
    if (user?.role === 'main_admin' && !user?.isMainAdminSwitch && location.pathname.startsWith('/dashboard')) {
      navigate('/main-admin', { replace: true });
    }
  }, [user, location.pathname, navigate]);


  // Check if user can manage projects based on permissions OR position hierarchy
  const canManageProjects = () => {
    // Main admin and org admin can always manage projects
    if (user?.role === 'main_admin' || user?.role === 'org_admin') {
      return true;
    }
    
    // For org_user, check permissions only (position-based check removed)
    if (user?.role === 'org_user') {
      // If user has addProject permission, allow access
      if (user?.permissions?.addProject) {
        return true;
      }
      
      // Position-based check commented out - now only permission-based
      // Otherwise, check position hierarchy (keep existing logic as fallback)
      // const projectManagementPositions = [
      //   'CEO',
      //   'CTO',
      //   'VP',
      //   'Director',
      //   'Manager',
      //   'Project Manager',
      //   'Product Manager',
      //   'Scrum Master',
      //   'Engineering Manager',
      //   'Team Lead',
      //   'team lead',
      //   'team_lead',
      //   'Lead',
      //   'tech lead',
      //   'tech_lead'
      // ];

      // // Developer positions that should NOT manage projects
      // const juniorDeveloperPositions = [
      //   'intern',
      //   'junior',
      //   'jr'
      // ];

      // const userPosition = user?.position?.toLowerCase() || '';

      // // If position contains junior developer keywords, they can't manage projects
      // if (juniorDeveloperPositions.some(devPos => userPosition.includes(devPos))) {
      //   return false;
      // }

      // // Check if position contains management keywords
      // return projectManagementPositions.some(position =>
      //   userPosition.includes(position.toLowerCase())
      // );
      
      // Return false if no addProject permission
      return false;
    }
    
    return false;
  };

  // Compute restricted mode (free trial expired or paid plan invalid/expired)
  // Checks both user-level AND organization-level subscription expiry
  const restricted = React.useMemo(() => {
    if (!organization) return false;
    
    // Check if organization has payment bypass enabled - if yes, no restrictions
    if (organization.paymentBypassEnabled === true) {
      console.log('✅ [LAYOUT RESTRICTED] Payment bypass enabled - no restrictions', {
        paymentBypassEnabled: organization.paymentBypassEnabled,
        orgPlanSelection: organization.orgPlanSelection
      });
      return false; // No restrictions if bypass enabled
    }
    
    console.log('⚠️ [LAYOUT RESTRICTED] Payment bypass check:', {
      paymentBypassEnabled: organization.paymentBypassEnabled,
      orgPlanSelection: organization.orgPlanSelection,
      subscriptionStatus: organization.subscriptionStatus
    });
    
    const now = new Date();
    // If bypass enabled but orgPlanSelection is null, treat as premium (no restrictions)
    const effectivePlan = organization.paymentBypassEnabled ? 'premium' : organization.orgPlanSelection;
    const isFree = effectivePlan === 'free';
    
    // User-level subscription check
    const userStatus = organization.userSubscriptionStatus;
    const userEndDate = organization.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
    // Treat userSubscriptionEndDate as trialEnd for free plan (backend sets it)
    const freeExpired = isFree && userEndDate && now > userEndDate;
    const userPaidInvalid = !isFree && (
      ['past_due','canceled','incomplete_expired','unpaid','expired'].includes(userStatus) ||
      (userEndDate && now > userEndDate)
    );
    
    // Organization-level subscription check (for all members)
    const orgStatus = organization.subscriptionStatus; // Organization-level subscription status
    const orgPeriodEnd = organization.currentPeriodEnd ? new Date(organization.currentPeriodEnd) : null;
    // Fallback to trialEnd if currentPeriodEnd is not set and status is trialing
    const effectivePeriodEnd = orgPeriodEnd || (orgStatus === 'trialing' && organization.trialEnd ? new Date(organization.trialEnd) : null);
    
    // Check if subscription is actually active
    // Active means: periodEnd is in the future (not expired)
    const hasActiveSubscription = effectivePeriodEnd && now < effectivePeriodEnd;
    
    // Check if subscription is expired
    // IMPORTANT: If periodEnd is in future, subscription is active regardless of status
    // This handles the case where webhook hasn't updated status yet but periodEnd is updated
    const orgExpired = !isFree && !hasActiveSubscription && (
      ['past_due','canceled','incomplete_expired','unpaid','expired'].includes(orgStatus) ||
      (effectivePeriodEnd && now > effectivePeriodEnd) ||
      !effectivePeriodEnd // No period end means expired
    );
    
    // If organization subscription expired, restrict ALL members
    const isRestricted = Boolean(freeExpired || userPaidInvalid || orgExpired);
    
    console.log('🔍 [LAYOUT RESTRICTED] Final restricted check:', {
      restricted: isRestricted,
      freeExpired,
      userPaidInvalid,
      orgExpired,
      isFree,
      userStatus,
      orgStatus
    });
    
    return isRestricted;
  }, [organization]);

  // Navigation: restrict to Dashboard + Settings when in restricted mode
  const baseNav = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings },
  ];

  const fullNav = (user?.role === 'main_admin')
    ? [
        { name: 'Main Admin Panel', href: '/main-admin', icon: BarChart3 },
        { name: 'Error Logs', href: '/main-admin/error-logs', icon: AlertCircle }
      ]
      : [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        // Only show Goals & Tasks group for S5 org when at least one module is enabled (and settings are loaded)
        ...((goalsSettingsLoaded && isS5Organisation(user) && (goalsSettings?.myGoalsEnabled || goalsSettings?.myTasksEnabled)) ? [
          { 
            name: 'Goals & Tasks', 
            icon: BarChart3, 
            isGroup: true,
            children: [
              // Only include My Goals if enabled
              ...(goalsSettings?.myGoalsEnabled ? [
                { name: 'Goals', href: '/dashboard/goals', icon: Target }
              ] : []),
              // Only include My Tasks if enabled
              ...(goalsSettings?.myTasksEnabled ? [
                { name: 'Tasks', href: '/dashboard/tasks', icon: CheckSquare }
              ] : []),
            ]
          }
        ] : []),
        // { name: 'Team Goals', href: '/dashboard/reports', icon: BarChart3 },
        // Org Intelligence
        ...(organization?.paymentBypassEnabled || organization?.orgPlanSelection === 'premium' || organization?.orgPlanSelection === 'free') ? [
          { name: 'Org Intelligence', href: '/dashboard/brainops', icon: Brain, locked: false }
        ] : organization?.orgPlanSelection === 'standard' ? [
          { name: 'Org Intelligence', href: '/dashboard/brainops', icon: Brain, locked: true }
        ] : [],
        { name: 'Work Summary', href: '/dashboard/daily-summary', icon: Calendar },
        { name: 'Manage Projects', href: '/dashboard/projects', icon: FolderOpen },
        { name: 'Team Management', href: '/dashboard/team', icon: Users },
        // Monthly Summary - Hidden from side nav
        // ...(user?.email !== 'j5@gmail.com' ? (
        //   (organization?.paymentBypassEnabled || organization?.orgPlanSelection === 'standard' || organization?.orgPlanSelection === 'premium') ? [
        //     { name: 'Monthly Summary', href: '/dashboard/monthly-summary', icon: FileText }
        //   ] : []
        // ) : []),
        // Show Chat for all users
        { name: 'Ask TeamGrid', href: '/dashboard/ai-search', icon: MessageCircle, badge: 'Beta' },
        { name: 'Email Insights', href: '/dashboard/email-insights', icon: Mail },
        // CRM nav is org-gated AND per-user gated:
        //  - Org must have crmEnabled=true (set by super-admin in admin panel)
        //  - User must be admin OR have permissions.crmAccess === true
        // For all other orgs/users the CRM nav item is completely absent.
        ...(canUserAccessCrm(user, organization) ? [
          { name: 'CRM', href: '/dashboard/crm/preview', icon: Briefcase, badge: 'Preview' }
        ] : []),
        // Employee Activity is NOT a nav item — it's opened from a button on the Dashboard
        // (EmployeeActivityModal). The /dashboard/employee-activity page remains as a
        // direct-URL fallback only.
        { name: 'Settings', href: '/dashboard/settings', icon: Settings },
      ];

  // Until organization loads, default to minimal nav to avoid flash of full menu
  const navigation = (!orgLoaded) ? baseNav : (restricted ? baseNav : fullNav);

  // Redirect to dashboard if plan expired and user is on a different page
  useEffect(() => {
    if (restricted && orgLoaded && location.pathname !== '/dashboard' && location.pathname !== '/dashboard/settings') {
      console.log('🔄 [LAYOUT] Plan expired - redirecting to dashboard', {
        currentPath: location.pathname,
        restricted
      });
      navigate('/dashboard', { replace: true });
    }
  }, [restricted, orgLoaded, location.pathname, navigate]);

  // Redirect if user is on a disabled Goals/Tasks page or not in S5 org
  useEffect(() => {
    if (!orgLoaded || restricted) return;
    const s5Only = isS5Organisation(user);

    // Redirect from /dashboard/goals if not S5 or My Goals is disabled
    if (location.pathname === '/dashboard/goals' && (!s5Only || !goalsSettings?.myGoalsEnabled)) {
      navigate('/dashboard', { replace: true });
      if (!s5Only) toast.error('Goals are not available for your organisation');
      else toast.error('My Goals module is disabled');
    }

    // Redirect from /dashboard/tasks if not S5 or My Tasks is disabled.
    // ADDITIVE: skip when the org has the new Tasks module enabled — that route
    // now belongs to modules/tasks (org-gated), not the Goals area.
    if (organization?.tasksEnabled !== true
      && location.pathname.startsWith('/dashboard/tasks') && (!s5Only || !goalsSettings?.myTasksEnabled)) {
      navigate('/dashboard', { replace: true });
      if (!s5Only) toast.error('Tasks are not available for your organisation');
      else toast.error('My Tasks module is disabled');
    }
  }, [location.pathname, goalsSettings, orgLoaded, restricted, navigate, user, organization]);

  // Dev-only navigation — main_admin / active switch only (never plain org_admin)
  // Team Activity (/dashboard/activity) is hidden.
  const devNavigation = [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigation = (href, locked = false) => {
    // If item is locked (Standard plan Org Intelligence), show upgrade prompt
    if (locked) {
      toast.error('Org Intelligence is only available on Premium or Free plan. Please upgrade to access this feature.', {
        duration: 4000,
        icon: '🔒',
      });
      navigate('/dashboard/settings?section=billing');
      setSidebarOpen(false);
      return;
    }

    // Don't navigate if already on this route
    if (location.pathname === href) {
      setSidebarOpen(false);
      return;
    }

    // Guard: if leaving settings while Goal Settings has unsaved changes
    const isLeavingSettings =
      location.pathname === '/dashboard/settings' &&
      new URLSearchParams(location.search).get('section') === 'goals_settings';

    if (isLeavingSettings && hasUnsavedGoalsChanges) {
      pendingNavHref.current = href;
      setShowUnsavedModal(true);
      setSidebarOpen(false);
      return;
    }

    navigate(href);
    setSidebarOpen(false);
  };

  const proceedNavigation = () => {
    if (pendingNavHref.current) {
      navigate(pendingNavHref.current);
      pendingNavHref.current = null;
    }
    setSidebarOpen(false);
  };

  const handleUnsavedLeave = () => {
    resetGoalsToSaved?.();
    setShowUnsavedModal(false);
    proceedNavigation();
  };

  const handleUnsavedSaveAndLeave = async () => {
    setSavingOnLeave(true);
    try {
      await saveGoalsSettings?.();
    } catch (_) {
      // errors already toasted by saveSettings
    } finally {
      setSavingOnLeave(false);
    }
    setShowUnsavedModal(false);
    proceedNavigation();
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedModal(false);
    pendingNavHref.current = null;
  };

  const isActive = useCallback((href) => {
    return location.pathname === href;
  }, [location.pathname]);

  // Render sidebar directly - React will handle re-renders efficiently
  // Active state updates via CSS classes which is fast

  return (
    <div className="h-screen flex overflow-hidden bg-app-base text-app-primary transition-colors duration-300">
      {/* Mobile menu */}
      <div className={`md:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 flex z-40">
          <div className="fixed inset-0 bg-slate-900/40 dark:bg-slate-900/60 transition-colors" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-app-surface border-r border-app-subtle">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-app-primary" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <button 
                  onClick={() => handleNavigation('/dashboard')}
                  className="flex items-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg p-1"
                  title="Go to Dashboard"
                >
                  <Logo size={36} showText={false} />
                </button>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {navigation.map((item) => {
                  if (item.isGroup) {
                    const Icon = item.icon;
                    const isGoalsActive = location.pathname === '/dashboard/goals' || location.pathname === '/dashboard/tasks';

                    // If only one child, render as a direct link (no dropdown)
                    if (item?.children?.length === 1) {
                      const singleChild = item.children[0];
                      const SingleIcon = singleChild.icon;
                      const singleActive = isActive(singleChild.href);
                      return (
                        <button
                          key={item.name}
                          onClick={() => handleNavigation(singleChild.href)}
                          className={`group flex items-center w-full px-3 py-2 text-base font-semibold rounded-md text-left transition-colors ${
                            singleActive
                              ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                              : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                          }`}
                        >
                          <SingleIcon
                            className={`mr-4 h-6 w-6 flex-shrink-0 transition-colors ${
                              singleActive
                                ? 'text-blue-600 dark:text-white'
                                : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                            }`}
                          />
                          <span className="flex-1 text-left">{singleChild.name}</span>
                          {singleActive && (
                            <div className="w-1 h-5 bg-blue-600 rounded-full" />
                          )}
                        </button>
                      );
                    }

                    // Multiple children - show dropdown
                    return (
                      <div key={item.name} className="space-y-1">
                        <button
                          onClick={() => setIsGoalsExpanded(!isGoalsExpanded)}
                          className={`group flex items-center justify-between w-full px-3 py-2 text-base font-semibold rounded-md text-left transition-colors ${
                            isGoalsActive
                              ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                              : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                          }`}
                        >
                          <div className="flex items-center">
                            <Icon
                              className={`mr-4 h-6 w-6 flex-shrink-0 transition-colors ${
                                isGoalsActive
                                  ? 'text-blue-600 dark:text-white'
                                  : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                              }`}
                            />
                            <span className="flex-1 text-left">{item.name}</span>
                          </div>
                          <ChevronDown
                            size={18}
                            className={`transition-transform duration-200 ${
                              isGoalsExpanded ? 'rotate-0' : '-rotate-90'
                            } ${isGoalsActive ? 'text-blue-600 dark:text-white' : 'text-app-secondary dark:text-gray-400'}`}
                          />
                        </button>
                        {isGoalsExpanded && (
                          <div className="ml-8 space-y-1">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              const childActive = isActive(child.href);
                              return (
                                <button
                                  key={child.name}
                                  onClick={() => handleNavigation(child.href)}
                                  className={`group flex items-center justify-start w-full px-3 py-2 text-base font-medium rounded-md text-left transition-colors ${
                                    childActive
                                      ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                                      : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                                  }`}
                                >
                                  <ChildIcon
                                    className={`mr-4 h-5 w-5 flex-shrink-0 transition-colors ${
                                      childActive
                                        ? 'text-blue-600 dark:text-white'
                                        : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                                    }`}
                                  />
                                  <span className="flex-1 text-left">{child.name}</span>
                                  {childActive && (
                                    <div className="w-1 h-5 bg-blue-600 rounded-full" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Regular navigation item
                  const Icon = item.icon;
                  const isLocked = item.locked === true;
                  return (
                    <button
                      key={item.name}
                      onClick={() => handleNavigation(item.href, isLocked)}
                      className={`group flex items-center justify-start w-full px-3 py-2 text-base font-medium rounded-md text-left transition-colors ${isActive(item.href)
                        ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                        : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                        } ${isLocked ? 'opacity-75 cursor-not-allowed' : ''}`}
                      disabled={isLocked}
                    >
                      <Icon
                        className={`mr-4 h-6 w-6 flex-shrink-0 transition-colors ${isActive(item.href) ? 'text-blue-600 dark:text-white' : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                          } ${isLocked ? 'opacity-60' : ''}`}
                      />
                      <span className="flex-1 text-left">{item.name}</span>
                      {isLocked && (
                        <Lock className="ml-2 h-4 w-4 text-gray-500 flex-shrink-0" />
                      )}
                      {item.badge && (
                        <span className="ml-2 px-1.5 py-0.5 bg-yellow-500/15 border border-yellow-400/25 rounded text-xs font-medium text-yellow-700 dark:text-yellow-300 flex-shrink-0">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
                
                {/* Switch Back to Main Admin - Show below Settings when in switch mode */}
                {user?.isMainAdminSwitch && user?.originalMainAdminId && localStorage.getItem('isMainAdminSwitch') === 'true' && (
                  <button
                    onClick={async () => {
                      try {
                        const response = await api.post('/api/v1/admin/switch-back');
                        if (response.data.success) {
                          localStorage.setItem('token', response.data.data.token);
                          localStorage.setItem('refreshToken', response.data.data.refreshToken);
                          // Clear switch session flags
                          localStorage.removeItem('isMainAdminSwitch');
                          localStorage.removeItem('mainAdminSwitchUserId');
                          toast.success('Switched back to Main Admin');
                          window.location.href = '/main-admin';
                        }
                      } catch (error) {
                        toast.error('Failed to switch back');
                      }
                    }}
                    className="w-full flex items-center justify-start px-3 py-2 text-base font-medium rounded-md bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-500/20 transition-colors"
                  >
                    <ArrowLeft className="mr-4 h-6 w-6 flex-shrink-0" />
                    <span className="flex-1 text-left">Back to Main Admin</span>
                  </button>
                )}
              </nav>
              
              {/* Dev-only navigation section */}
              {devNavigation.length > 0 && (
                <>
                  <div className="mt-6 px-2">
                    <div className="text-xs font-semibold text-app-secondary uppercase tracking-wider px-2 py-1">
                      For Dev only
                    </div>
                  </div>
                  <nav className="px-2 space-y-1">
                    {devNavigation.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.name}
                          onClick={() => handleNavigation(item.href)}
                          className={`group flex items-center w-full px-2 py-2 text-base font-medium rounded-md transition-colors ${isActive(item.href)
                            ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                            : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                            }`}
                        >
                          <Icon className={`mr-4 h-6 w-6 transition-colors ${isActive(item.href) ? 'text-blue-600 dark:text-white' : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'}`} />
                          <span className="flex-1 text-left">{item.name}</span>
                          {item.badge && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white">
                              {item.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                </>
              )}
            </div>
            <div className="flex-shrink-0 border-t border-app-subtle dark:border-white/10">
              {/* Download App Section */}
              <div className="p-4 border-b border-app-subtle dark:border-white/10">
                <button
                  onClick={() => handleNavigation('/download')}
                  className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-app-secondary hover:text-app-primary hover:bg-app-muted dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5 rounded-md transition-colors group"
                >
                  <Download className="h-4 w-4 mr-2 text-app-secondary group-hover:text-emerald-500 transition-colors" />
                  Download Desktop App
                </button>
              </div>
              
              {/* User Profile */}
              <div className="p-4 space-y-3">
                <div className="flex items-center">
                  <div>
                    <div className="text-sm font-medium text-app-primary dark:text-white">
                      {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email}
                    </div>
                    <div className="text-xs text-app-secondary dark:text-gray-400">{user?.role}</div>
                    {user?.isMainAdminSwitch && user?.originalMainAdminId && localStorage.getItem('isMainAdminSwitch') === 'true' && (
                      <div className="text-xs text-yellow-600 dark:text-yellow-300 mt-1">Main Admin Mode</div>
                    )}
                  </div>
                  <button
                    onClick={toggle}
                    className="ml-auto mr-2 flex-shrink-0 p-1.5 rounded-full border border-app-subtle hover:border-app-strong bg-app-muted/80 hover:bg-app-muted transition-colors"
                    title={`Switch to ${isDark ? 'Light' : 'Dark'} mode`}
                  >
                    {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex-shrink-0 bg-app-muted p-1 rounded-full text-app-secondary hover:text-app-primary dark:bg-white/10 dark:text-gray-400 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                  >
                    <LogOut className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>
            

          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className={`relative flex flex-col transition-[width] duration-200 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}>
          {/* Edge toggle — sits on the sidebar's right border so it's reachable in both states */}
          <button
            onClick={toggleSidebarCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute top-6 -right-3 z-20 h-6 w-6 rounded-full border border-app-subtle bg-app-surface text-app-secondary hover:text-app-primary hover:bg-app-muted dark:border-white/10 dark:text-gray-400 dark:hover:text-white shadow-sm flex items-center justify-center transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </button>
          <div className="flex flex-col h-0 flex-1 border-r border-app-subtle dark:border-white/10 bg-app-surface">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className={`flex items-center flex-shrink-0 ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'}`}>
                <button
                  onClick={() => handleNavigation('/dashboard')}
                  className="flex items-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg p-1"
                  title="Go to Dashboard"
                >
                  {sidebarCollapsed ? (
                    <img src="/favicon.webp" alt="TeamGrid" className="h-9 w-9 object-contain" />
                  ) : (
                    <Logo size={36} showText={false} />
                  )}
                </button>
              </div>
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {navigation.map((item) => {
                  if (item.isGroup) {
                    const Icon = item.icon;
                    const isGoalsActive = location.pathname === '/dashboard/goals' || location.pathname === '/dashboard/tasks';

                    // If only one child, render as a direct link (no dropdown)
                    if (item?.children?.length === 1) {
                      const singleChild = item.children[0];
                      const SingleIcon = singleChild.icon;
                      const singleActive = isActive(singleChild.href);
                      return (
                        <button
                          key={item.name}
                          onClick={() => handleNavigation(singleChild.href)}
                          title={sidebarCollapsed ? singleChild.name : undefined}
                          className={`group flex items-center w-full ${sidebarCollapsed ? 'justify-center px-2' : 'px-3'} py-2 text-sm font-semibold rounded-md text-left transition-colors ${
                            singleActive
                              ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                              : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                          }`}
                        >
                          <SingleIcon
                            className={`${sidebarCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0 transition-colors ${
                              singleActive
                                ? 'text-blue-600 dark:text-white'
                                : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                            }`}
                          />
                          {!sidebarCollapsed && (
                            <>
                              <span className="flex-1 text-left">{singleChild.name}</span>
                              {singleActive && (
                                <div className="w-1 h-4 bg-blue-600 rounded-full" />
                              )}
                            </>
                          )}
                        </button>
                      );
                    }

                    // Multiple children - in collapsed mode, render each child as a flat icon
                    // (no dropdown trigger makes sense when there's no room for the panel).
                    if (sidebarCollapsed) {
                      return (
                        <React.Fragment key={item.name}>
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            const childActive = isActive(child.href);
                            return (
                              <button
                                key={child.name}
                                onClick={() => handleNavigation(child.href)}
                                title={child.name}
                                className={`group flex items-center justify-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                                  childActive
                                    ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                                    : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                                }`}
                              >
                                <ChildIcon
                                  className={`h-5 w-5 flex-shrink-0 transition-colors ${
                                    childActive
                                      ? 'text-blue-600 dark:text-white'
                                      : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                                  }`}
                                />
                              </button>
                            );
                          })}
                        </React.Fragment>
                      );
                    }

                    return (
                      <div key={item.name} className="space-y-1">
                        <button
                          onClick={() => setIsGoalsExpanded(!isGoalsExpanded)}
                          className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-semibold rounded-md text-left transition-colors ${
                            isGoalsActive
                              ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                              : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                          }`}
                        >
                          <div className="flex items-center">
                            <Icon
                              className={`mr-3 h-5 w-5 flex-shrink-0 transition-colors ${
                                isGoalsActive
                                  ? 'text-blue-600 dark:text-white'
                                  : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                              }`}
                            />
                            <span className="flex-1 text-left">{item.name}</span>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${
                              isGoalsExpanded ? 'rotate-0' : '-rotate-90'
                            } ${isGoalsActive ? 'text-blue-600 dark:text-white' : 'text-app-secondary dark:text-gray-400'}`}
                          />
                        </button>
                        {isGoalsExpanded && (
                          <div className="ml-8 space-y-1">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              const childActive = isActive(child.href);
                              return (
                                <button
                                  key={child.name}
                                  onClick={() => handleNavigation(child.href)}
                                  className={`group flex items-center justify-start w-full px-3 py-2 text-sm font-medium rounded-md text-left transition-colors ${
                                    childActive
                                      ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                                      : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                                  }`}
                                >
                                  <ChildIcon
                                    className={`mr-3 h-4 w-4 flex-shrink-0 transition-colors ${
                                      childActive
                                        ? 'text-blue-600 dark:text-white'
                                        : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                                    }`}
                                  />
                                  <span className="flex-1 text-left">{child.name}</span>
                                  {childActive && (
                                    <div className="w-1 h-4 bg-blue-600 rounded-full" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Regular navigation item
                  const Icon = item.icon;
                  const isLocked = item.locked === true;
                  const active = isActive(item.href);
                  return (
                    <button
                      key={item.name}
                      onClick={() => handleNavigation(item.href, isLocked)}
                      title={sidebarCollapsed ? item.name : undefined}
                      className={`group flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} w-full py-2 text-sm font-medium rounded-md text-left transition-colors ${active
                        ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                        : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                        } ${isLocked ? 'opacity-75 cursor-not-allowed' : ''}`}
                      disabled={isLocked}
                    >
                      <Icon
                        className={`${sidebarCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0 transition-colors ${active ? 'text-blue-600 dark:text-white' : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'
                          } ${isLocked ? 'opacity-60' : ''}`}
                      />
                      {!sidebarCollapsed && (
                        <>
                          <span className="flex-1 text-left">{item.name}</span>
                          {isLocked && (
                            <Lock className="ml-2 h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                          )}
                          {item.badge && !isLocked && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-blue-500 !text-white flex-shrink-0">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
                
                {/* Switch Back to Main Admin */}
                {user?.isMainAdminSwitch && user?.originalMainAdminId && localStorage.getItem('isMainAdminSwitch') === 'true' && (
                  <button
                    onClick={async () => {
                      try {
                        const response = await api.post('/api/v1/admin/switch-back');
                        if (response.data.success) {
                          localStorage.setItem('token', response.data.data.token);
                          localStorage.setItem('refreshToken', response.data.data.refreshToken);
                          // Clear switch session flags
                          localStorage.removeItem('isMainAdminSwitch');
                          localStorage.removeItem('mainAdminSwitchUserId');
                          toast.success('Switched back to Main Admin');
                          window.location.href = '/main-admin';
                        }
                      } catch (error) {
                        toast.error('Failed to switch back');
                      }
                    }}
                    title={sidebarCollapsed ? 'Back to Main Admin' : undefined}
                    className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} py-2 text-sm font-medium rounded-md text-yellow-700 dark:text-yellow-300 bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors`}
                  >
                    <ArrowLeft className={`${sidebarCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0`} />
                    {!sidebarCollapsed && <span className="flex-1 text-left">Back to Main Admin</span>}
                  </button>
                )}
              </nav>

              {/* Dev-only navigation section */}
              {devNavigation.length > 0 && (
                <>
                  {!sidebarCollapsed && (
                    <div className="mt-6 px-2">
                      <div className="text-xs font-semibold text-app-secondary uppercase tracking-wider px-2 py-1">
                        For Dev only
                      </div>
                    </div>
                  )}
                  <nav className={`${sidebarCollapsed ? 'mt-3 border-t border-app-subtle dark:border-white/10 pt-3' : ''} px-2 space-y-1`}>
                    {devNavigation.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      return (
                        <button
                          key={item.name}
                          onClick={() => handleNavigation(item.href)}
                          title={sidebarCollapsed ? item.name : undefined}
                          className={`group flex items-center w-full ${sidebarCollapsed ? 'justify-center' : ''} px-2 py-2 text-sm font-medium rounded-md transition-colors ${active
                            ? 'bg-app-muted text-app-primary dark:bg-white/10 dark:text-white'
                            : 'text-app-secondary hover:bg-app-muted hover:text-app-primary dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                            }`}
                        >
                          <Icon className={`${sidebarCollapsed ? '' : 'mr-3'} h-5 w-5 transition-colors ${active ? 'text-blue-600 dark:text-white' : 'text-app-secondary dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-white'}`} />
                          {!sidebarCollapsed && item.name}
                        </button>
                      );
                    })}
                  </nav>
                </>
              )}
            </div>
            <div className="flex-shrink-0 border-t border-app-subtle dark:border-white/10">
              {/* Download App Section */}
              <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-b border-app-subtle dark:border-white/10`}>
                <button
                  onClick={() => handleNavigation('/download')}
                  title={sidebarCollapsed ? 'Download Desktop App' : undefined}
                  className={`w-full flex items-center justify-center ${sidebarCollapsed ? 'px-2' : 'px-3'} py-2 text-sm font-medium text-app-secondary hover:text-app-primary hover:bg-app-muted dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5 rounded-md transition-colors group`}
                >
                  <Download className={`h-4 w-4 ${sidebarCollapsed ? '' : 'mr-2'} text-app-secondary group-hover:text-emerald-500 transition-colors`} />
                  {!sidebarCollapsed && 'Download Desktop App'}
                </button>
              </div>

              {/* User Profile */}
              <div className={`flex items-center w-full ${sidebarCollapsed ? 'flex-col gap-2 p-2' : 'p-4'}`}>
                {!sidebarCollapsed && (
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-sm font-medium text-app-primary dark:text-white truncate"
                      title={user?.name || user?.email}
                    >
                      {user?.name || user?.email}
                    </div>
                    <div className="text-xs text-app-secondary dark:text-gray-400 truncate">
                      {user?.role}
                      {user?.isMainAdminSwitch && user?.originalMainAdminId && localStorage.getItem('isMainAdminSwitch') === 'true' && (
                        <span className="ml-2 text-yellow-600 dark:text-yellow-300">(Main Admin Mode)</span>
                      )}
                    </div>
                  </div>
                )}
                <div className={`flex ${sidebarCollapsed ? 'flex-col gap-1' : 'space-x-2'} flex-shrink-0`}>
                  <button
                    onClick={toggle}
                    className="p-2 rounded-md border border-app-subtle hover:border-app-strong bg-app-muted/70 hover:bg-app-muted text-app-secondary hover:text-app-primary dark:bg-white/5 dark:text-gray-300 dark:hover:text-white"
                    title={`Switch to ${isDark ? 'Light' : 'Dark'} mode`}
                  >
                    {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-md text-app-secondary hover:text-app-primary hover:bg-app-muted focus:outline-none focus:ring-2 focus:ring-offset-2 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5"
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <main ref={mainRef} className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          {/* Mobile menu button - only visible on mobile */}
          <div className="md:hidden fixed top-4 left-4 z-50">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md bg-app-surface/90 backdrop-blur-md border border-app-subtle p-2 text-app-secondary hover:text-app-primary transition-colors focus:outline-none focus:ring-2 focus:ring-inset"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
          <div
            id="app-main-content"
            className="h-full relative overflow-y-auto"
            // style={{ padding: '24px' }}
          >
            <Outlet />
          </div>
        </main>
      </div>
      
      {/* Floating Chat Button - Show for all users, hide on ai-search page */}
      {orgLoaded &&
       !restricted &&
       user &&
       user.role !== 'main_admin' &&
       location.pathname !== '/dashboard/ai-search' && (
        <FloatingChatButton />
      )}
      {orgLoaded &&
       !restricted &&
       user &&
       (user.role === 'org_admin' || user.isMainAdminSwitch) && (
        <FloatingSuggestButton />
      )}

      {/* Unsaved Goal Settings confirmation modal */}
      <Modal
        open={showUnsavedModal}
        onCancel={handleUnsavedCancel}
        footer={null}
        centered
        closable={false}
        width={440}
        styles={{
          content: {
            background: isDark ? '#0f1117' : '#ffffff',
            border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
            borderRadius: '20px',
            padding: '0',
            overflow: 'hidden',
            boxShadow: isDark
              ? '0 25px 50px rgba(0,0,0,0.6)'
              : '0 25px 50px rgba(0,0,0,0.12)',
          },
          mask: { backdropFilter: 'blur(4px)', background: 'rgba(0,0,0,0.45)' }
        }}
      >
        {/* Amber top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />

        <div className="px-7 pt-6 pb-7 flex flex-col gap-5">
          {/* Icon + heading */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-500" strokeWidth={2.2} />
            </div>
            <div className="pt-0.5">
              <h3 className={`text-[15px] font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                You have unsaved changes
              </h3>
              <p className={`text-[13px] mt-1 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Your Goal Settings edits haven't been saved yet. What would you like to do?
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className={`h-px w-full ${isDark ? 'bg-white/[0.06]' : 'bg-gray-100'}`} />

          {/* Action cards */}
          <div className="flex flex-col gap-2.5">
            <button
              onClick={handleUnsavedSaveAndLeave}
              disabled={savingOnLeave}
              className={`group w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border text-left transition-all duration-150 ${
                isDark
                  ? 'bg-blue-600/10 border-blue-500/25 hover:bg-blue-600/20 hover:border-blue-500/40'
                  : 'bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-sm">
                <Save size={14} className="!text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className={`text-[13px] font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  {savingOnLeave ? 'Saving...' : 'Save changes & leave'}
                </p>
                <p className={`text-[11px] mt-0.5 ${isDark ? 'text-blue-400/60' : 'text-blue-500/70'}`}>
                  Save your edits, then navigate away
                </p>
              </div>
            </button>

            <button
              onClick={handleUnsavedLeave}
              className={`group w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border text-left transition-all duration-150 ${
                isDark
                  ? 'bg-white/[0.03] border-white/[0.07] hover:bg-red-500/10 hover:border-red-500/20'
                  : 'bg-gray-50 border-gray-200 hover:bg-red-50 hover:border-red-200'
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${
                isDark
                  ? 'bg-white/5 border-white/10 group-hover:bg-red-500/20 group-hover:border-red-500/30'
                  : 'bg-white border-gray-200 group-hover:bg-red-100 group-hover:border-red-200'
              }`}>
                <X size={14} className={`transition-colors ${isDark ? 'text-gray-400 group-hover:text-red-400' : 'text-gray-400 group-hover:text-red-500'}`} strokeWidth={2.5} />
              </div>
              <div>
                <p className={`text-[13px] font-semibold transition-colors ${isDark ? 'text-gray-300 group-hover:text-red-400' : 'text-gray-700 group-hover:text-red-600'}`}>
                  Discard changes & leave
                </p>
                <p className={`text-[11px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Your unsaved edits will be lost
                </p>
              </div>
            </button>
          </div>

          {/* Cancel button */}
          <button
            onClick={handleUnsavedCancel}
            className={`w-full py-2.5 rounded-xl text-[13px] font-medium border transition-all duration-150 ${
              isDark
                ? 'bg-transparent border-white/10 text-gray-400 hover:bg-white/5 hover:text-gray-200 hover:border-white/20'
                : 'bg-transparent border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Keep editing
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Memoize Layout to prevent re-renders when only location changes
// Layout should only re-render when user, organization, or theme changes
export default React.memo(Layout);
